import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useLoader } from '@/contexts/LoaderContext';
import Home from '@/pages/Home';
import HomeWhitespace from '@/pageswhitespace/HomeWhitespace';
import DynamicPage from '@/components/DynamicPage';

/**
 * HomeGateway Component
 *
 * V3 behaviour:
 *   On mount, calls /api/journey/resolve/ with the detected bap_id.
 *   If the API returns a pre-login page (is_pre_login=true), renders
 *   DynamicPage instead of the hardcoded Home/HomeWhitespace.
 *   Falls back to legacy Home/HomeWhitespace if no pre-login journey is
 *   configured for this BAP.
 *
 * Detection Methods (in order of priority):
 *   1. URL parameter: enc_id === 'WHITESPACE'
 *   2. URL parameter: enc_id present and valid (whitespace BAP context)
 *   3. Session-stored BAP identifier
 *   4. Default: regular Home
 */

interface PreLoginPage {
  id: number;
  name: string;
  slug: string;
  route: string;
  components: string[];
  layout: Record<string, any>;
  is_pre_login: boolean;
  requires_auth: boolean;
}

interface ResolveJourneyResponse {
  journey_config_id: number;
  page: PreLoginPage;
}

async function resolvePreLoginJourney(
  bapId?: string | number | null,
  lenderId?: string | number | null
): Promise<ResolveJourneyResponse | null> {
  const body: Record<string, any> = {};
  if (bapId) body.bap_id = Number(bapId);
  if (lenderId) body.lender_id = Number(lenderId);
  if (!bapId && !lenderId) return null;

  try {
    const res = await fetch('/api/journey/resolve/', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (res.status === 204 || res.status === 404) return null;
    if (!res.ok) return null;
    return await res.json();
  } catch {
    return null;
  }
}

const HomeGateway = () => {
  const { enc_id } = useParams<{ enc_id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setLoading } = useLoader();

  const [detectedWhitespace, setDetectedWhitespace] = useState<boolean | null>(null);
  const [preLoginJourney, setPreLoginJourney] = useState<ResolveJourneyResponse | null>(null);
  const [resolving, setResolving] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    determineJourneyType();
  }, [enc_id, searchParams]);

  const determineJourneyType = async () => {
    setResolving(true);
    try {
      // METHOD 1: Direct URL parameter check
      if (enc_id === 'WHITESPACE') {
        extractAndStoreBAPInfo();
        setDetectedWhitespace(true);

        // V3: Try to resolve a pre-login journey for this BAP
        const bapNumericId = _getBapNumericId();
        if (bapNumericId) {
          const resolved = await resolvePreLoginJourney(bapNumericId);
          if (resolved) {
            setPreLoginJourney(resolved);
            // Store journey_config_id for use after OTP login
            sessionStorage.setItem(
              'pre_login_journey_config_id',
              String(resolved.journey_config_id)
            );
          }
        }
        return;
      }

      // METHOD 2: enc_id present (non-WHITESPACE)
      if (enc_id) {
        clearBAPInfo();
        setDetectedWhitespace(false);
        return;
      }

      // METHOD 3: No enc_id — check for BAP in query params
      const rawBapId = searchParams.get('bap_id') || searchParams.get('bap') || searchParams.get('bap_name');
      if (rawBapId) {
        extractAndStoreBAPInfo();
        // V3: Try to resolve a pre-login journey for this BAP name
        // Note: bap_id here is a string name, not a numeric DB id.
        // The resolve API accepts numeric IDs only; skip if non-numeric.
        const numericBapId = Number(rawBapId);
        if (!isNaN(numericBapId) && numericBapId > 0) {
          const resolved = await resolvePreLoginJourney(numericBapId);
          if (resolved) {
            setPreLoginJourney(resolved);
            sessionStorage.setItem(
              'pre_login_journey_config_id',
              String(resolved.journey_config_id)
            );
          }
        }
        setDetectedWhitespace(false);
        return;
      }

      // METHOD 4: No enc_id, no BAP — regular home
      clearBAPInfo();
      setDetectedWhitespace(false);
    } catch (err) {
      console.error('Error determining journey type:', err);
      setError('Failed to determine journey type');
      setDetectedWhitespace(false);
    } finally {
      setResolving(false);
    }
  };

  /** Get numeric BAP ID from sessionStorage or query params if available. */
  const _getBapNumericId = (): number | null => {
    const stored = sessionStorage.getItem('whitespace_bap_numeric_id');
    if (stored) return Number(stored);
    const fromQuery = searchParams.get('bap_numeric_id') || searchParams.get('bap_db_id');
    if (fromQuery) return Number(fromQuery);
    return null;
  };

  /**
   * Extract BAP information from query parameters and save to sessionStorage.
   * Supports multiple parameter names: bap_id, bap, bap_name
   */
  const extractAndStoreBAPInfo = () => {
    try {
      const rawBapId = searchParams.get('bap_id') || searchParams.get('bap') || searchParams.get('bap_name');
      const bapId = rawBapId?.trim();

      if (bapId && bapId !== 'WHITESPACE') {
        sessionStorage.setItem('whitespace_bap_id', bapId);
        console.log('[HomeGateway] BAP ID stored in sessionStorage:', bapId);

        let bapLogoUrl = `/assets/bap-logos/${bapId.toLowerCase()}.png`;

        if (bapId === 'PetPooja')    bapLogoUrl = 'https://www.intellendtech.com/data/pet-pooja.png';
        if (bapId === 'GoKwik')      bapLogoUrl = 'https://www.intellendtech.com/data/gokwik-logo.jpg';
        if (bapId === 'Tohands')     bapLogoUrl = 'https://www.intellendtech.com/data/TOHANDS.jpg';
        if (bapId === 'Easebuzz')    bapLogoUrl = 'https://www.intellendtech.com/data/easebuzz.png';
        if (bapId === '121Mapping')  bapLogoUrl = 'https://www.intellendtech.com/data/121mapping.png';
        if (bapId === 'MOB')         bapLogoUrl = 'https://www.intellendtech.com/data/mob.png';
        if (bapId === 'snapdeal')    bapLogoUrl = 'https://www.intellendtech.com/data/snapdeal.png';

        sessionStorage.setItem('whitespace_bap_logo', bapLogoUrl);

        const bapMetadata = {
          bap_id: bapId,
          logo_url: bapLogoUrl,
          timestamp: new Date().toISOString(),
        };
        sessionStorage.setItem('whitespace_bap_metadata', JSON.stringify(bapMetadata));
      } else {
        sessionStorage.removeItem('whitespace_bap_id');
        sessionStorage.removeItem('whitespace_bap_logo');
        sessionStorage.removeItem('whitespace_bap_metadata');
      }
    } catch (err) {
      console.warn('[HomeGateway] Error extracting BAP info:', err);
    }
  };

  const clearBAPInfo = () => {
    sessionStorage.removeItem('whitespace_bap_id');
    sessionStorage.removeItem('whitespace_bap_logo');
    sessionStorage.removeItem('whitespace_bap_metadata');
    sessionStorage.removeItem('pre_login_journey_config_id');
  };

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  if (error) {
    return (
      <div className="min-h-screen bg-intellend-white flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-intellend-green mb-4">Error</h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/')}
            className="bg-intellend-green text-white px-6 py-2 rounded-md hover:bg-intellend-green/90"
          >
            Go to Home
          </button>
        </div>
      </div>
    );
  }

  // Still resolving
  if (resolving || detectedWhitespace === null) {
    return <div className="min-h-screen bg-intellend-white" />;
  }

  // V3: Pre-login journey configured for this BAP — render dynamic page
  if (preLoginJourney) {
    console.log(
      '[HomeGateway] Rendering V3 pre-login page:',
      preLoginJourney.page.slug,
      'for journey_config_id:',
      preLoginJourney.journey_config_id
    );
    return <DynamicPage />;
  }

  // Legacy: whitespace BAP
  if (detectedWhitespace) {
    return <HomeWhitespace />;
  }

  // Legacy: regular home
  return <Home />;
};

export default HomeGateway;
